<template>
  <section class="estimator-form" data-testid="estimator-form">
    <!--
      Every input on this form belongs to a register, so with none there is
      nothing to fill in. Say so: the controls below would otherwise render on
      their own and read as a form still loading.
    -->
    <SoAlert
      v-if="!registers.length"
      type="info"
      :text="noRegistersMessage"
      data-testid="estimator-no-registers"
    />

    <template v-else>
      <EstimatorRegisterFields
        v-for="(registerState, index) in registerStates"
        :key="registerState.registerId"
        :meterpoint="meterpoint"
        :register="registers[index]"
        :state="registerState"
        :mode="mode"
        :show-register-name="registers.length > 1"
        class="mb-4"
        @patch="patchRegister(index, $event)"
      />

      <div class="estimator-controls">
        <SoDatePicker
          v-if="showTargetDate"
          v-model="targetDate"
          label="Target date"
          data-testid="estimator-target-date"
        />
        <!--
          Metering, Billing and Payments only. Everyone else gets seasonal estimates with
          no way to turn it off: a non-seasonal figure is for checking a past
          calculation, and in UAT one "matched Excel" only because the switch
          had been left off.
        -->
        <div v-if="canToggleSeasonality" class="estimator-controls__toggle">
          <SoToggle
            v-model="seasonal"
            label="Apply seasonality"
            data-testid="estimator-seasonal"
          />
          <SoTooltip :text="seasonalityTooltip">
            <SoIcon
              icon="info sm"
              color="info"
              class="ml-1"
              data-testid="estimator-seasonal-info"
            />
          </SoTooltip>
        </div>
      </div>

      <p v-if="isLoading" class="text-caption" data-testid="estimator-loading">
        Calculating…
      </p>

      <EstimatorResult
        :rows="resultRows"
        :validation-errors="validationErrors"
        :warnings="warnings"
        :request-failed="requestFailed"
        :shows-read="showsRead"
        :seasonal="appliesSeasonality"
        :eac-or-aq="eacOrAq"
        :volume-unit-label="volumeUnitLabel"
        :calorific-value-applied="calorificValueApplied"
      />

      <EstimatorSubmitRead
        v-if="showSubmitRead && lastRequest"
        :rows="resultRows"
        :registers="registers"
        :meterpoint="meterpoint"
        :target-date="lastRequest.targetDate"
        :calculation-detail="calculationDetail"
      />
    </template>
  </section>
</template>

<script lang="ts">
  import { defineComponent, PropType } from "vue"
  import dayjs from "dayjs"
  import { debounce, isEqual } from "lodash"
  import type { DebouncedFunc } from "lodash"
  import SoAlert from "@/components/SoAlert.vue"
  import SoIcon from "@/components/SoIcon.vue"
  import SoTooltip from "@/components/SoTooltip.vue"
  import SoDatePicker from "@/components/formControls/SoDatePicker.vue"
  import SoToggle from "@/components/formControls/SoToggle.vue"
  import EstimatorRegisterFields from "./EstimatorRegisterFields.vue"
  import EstimatorResult from "./EstimatorResult.vue"
  import EstimatorSubmitRead from "./EstimatorSubmitRead.vue"
  import IPermission from "@/types/IPermissions"
  import { IMeterpoint } from "@/models/meterpoint/meterpoint.types"
  import { IRegister } from "@/models/meterRegister/meterRegister.types"
  import {
    IEstimateRegisterInput,
    IEstimateRequest,
    IMeterReadEstimate,
    VOLUME_UNIT_LABELS,
  } from "@/models/meterReadEstimate/meterReadEstimate.types"
  import { MeterReadEstimatorService } from "@/services/MeterReadEstimatorService"
  import { formatDateDashed } from "@/helpers/dateHelper"
  import {
    DISPLAY_DECIMAL_PLACES,
    ESTIMATOR_MODE_TITLES,
    EstimatorUiMode,
    eacOrAqLabel,
    isCoarseVolumeUnit,
    isSameRegisterSet,
    isShortGap,
    toApiMode,
    toFuelType,
    usesTargetDate,
    usesTwoReads,
    volumeUnitFor,
  } from "./estimatorHelper"
  import { IRegisterFormState, RegisterFormPatch } from "./estimatorForm.types"
  import { buildResultRows, registerApiName } from "./estimatorResultRows"
  import {
    ESTIMATOR_PERMISSIONS,
    SEASONALITY_GUIDANCE,
    buildCalculationDetail,
    canSubmitRows,
  } from "./estimatorSystemRead"

  /** Long enough that typing a five-digit read is one request, not five. */
  export const DEBOUNCE_MS = 400

  /** Match the payload to the value on screen, not the one behind it. */
  const toSentRead = (value: number) =>
    Number(value.toFixed(DISPLAY_DECIMAL_PLACES))

  export const ORDERING_MESSAGE =
    "The later read must be dated after the earlier read."
  export const EMPTY_READ_MESSAGE =
    "A read of zero is not a reading. Enter both read values, or pick them from the dropdowns."
  export const BACKDATED_MESSAGE =
    "The target date is before the read this estimates from, so it estimates backwards - expect a lower read and negative consumption."
  export const SHORT_GAP_MESSAGE =
    "The two reads are less than a month apart, so the annual figure is extrapolated from a short sample and may be unreliable."
  export const NO_METERS_MESSAGE =
    "This meter point has no meter on it, so there are no registers to estimate a read for."
  export const NO_REGISTERS_MESSAGE =
    "The meters on this meter point have no registers, so there is nothing to estimate a read for."
  export const UNIT_REQUIRED_MESSAGE =
    "This register's reads do not say what unit they are in, so pick one before estimating - guessing it wrong changes the answer by about a third."
  export const COARSE_UNIT_MESSAGE =
    "This register records in thousands of cubic metres, so each unit is 1,000 m³. The estimate is correctly scaled but coarse - a domestic meter moves by only a unit or two a year."

  /**
   * The daily weighting factors the estimator seasonalises with are held one
   * curve per fuel - `elec_weight` and `gas_weight` on `daily_weighting_factors`
   * - and looked up per request, not per register. So both rates of a two-rate
   * meter are shaped by the same curve. That matches the Excel estimators this
   * replaces, and is the current intended behaviour rather than a defect, but it
   * is not obvious from the switch, so it is stated. Rate-level profiles are
   * SO-32561.
   */
  export const SEASONALITY_CAVEAT =
    "Seasonality on a two-rate meter for Day and Night rates uses the same calculation."

  export default defineComponent({
    components: {
      EstimatorRegisterFields,
      EstimatorResult,
      EstimatorSubmitRead,
      SoAlert,
      SoDatePicker,
      SoIcon,
      SoToggle,
      SoTooltip,
    },
    inject: {
      permission: { from: "$permission", default: null },
    },
    props: {
      meterpoint: {
        type: Object as PropType<IMeterpoint>,
        required: true,
      },
      mode: {
        type: String as PropType<EstimatorUiMode>,
        required: true,
      },
    },
    data() {
      return {
        registerStates: [] as IRegisterFormState[],
        targetDate: new Date(),
        seasonal: true,
        estimate: null as IMeterReadEstimate | null,
        lastRequest: null as IEstimateRequest | null,
        requestFailed: false,
        isLoading: false,
        debouncedFetch: null as DebouncedFunc<() => Promise<void>> | null,
      }
    },
    computed: {
      registers(): IRegister[] {
        return this.meterpoint.registers
      },
      /**
       * IMeterpoint.registers flattens across the meter point's meters, so it
       * empties for two different reasons and the agent needs to know which -
       * a meter point with no meter is a data problem to chase, registers
       * missing off a meter that is there is a different one.
       */
      noRegistersMessage(): string {
        return this.meterpoint.meters.length
          ? NO_REGISTERS_MESSAGE
          : NO_METERS_MESSAGE
      },
      eacOrAq(): string {
        return eacOrAqLabel(this.meterpoint)
      },
      /**
       * The guidance on every meter; the shared-profile caveat only where there
       * are two rates for one profile to be shared between.
       */
      seasonalityTooltip(): string {
        return this.registers.length > 1
          ? `${SEASONALITY_GUIDANCE}. ${SEASONALITY_CAVEAT}`
          : SEASONALITY_GUIDANCE
      },
      grantedPermission(): IPermission | null {
        return this.permission as IPermission | null
      },
      canToggleSeasonality(): boolean {
        return !!this.grantedPermission?.has(
          ESTIMATOR_PERMISSIONS.toggleSeasonality,
        )
      },
      canSubmitSystemRead(): boolean {
        return !!this.grantedPermission?.has(
          ESTIMATOR_PERMISSIONS.submitSystemRead,
        )
      },
      /** Without the permission the switch is gone, so seasonal it is. */
      appliesSeasonality(): boolean {
        return this.canToggleSeasonality ? this.seasonal : true
      },
      /**
       * Only a read estimate, only with seasonality on, and only while the
       * figure on screen is the answer to the inputs on screen - the request
       * moving on means a newer estimate is due and this one is stale.
       */
      showSubmitRead(): boolean {
        return (
          this.canSubmitSystemRead &&
          this.appliesSeasonality &&
          this.showsRead &&
          !this.isLoading &&
          !this.validationErrors.length &&
          this.request !== null &&
          isEqual(this.request, this.lastRequest) &&
          canSubmitRows(this.resultRows, this.registers)
        )
      },
      calculationDetail(): string {
        return buildCalculationDetail(this.resultRows, {
          modeTitle: ESTIMATOR_MODE_TITLES[this.mode].replace(
            "{eacOrAq}",
            this.eacOrAq,
          ),
          targetDate: this.lastRequest?.targetDate ?? "",
          seasonal: this.appliesSeasonality,
          volumeUnitLabel: this.volumeUnitLabel,
          calorificValueApplied: this.calorificValueApplied,
        })
      },
      showTargetDate(): boolean {
        return usesTargetDate(this.mode)
      },
      showsRead(): boolean {
        return this.mode !== EstimatorUiMode.EacAqFromTwoReads
      },
      orderingErrors(): string[] {
        const outOfOrder = this.registerStates.some((state) => {
          if (!usesTwoReads(this.mode)) return false
          if (!state.read1Date || !state.read2Date) return false

          return !dayjs(state.read2Date).isAfter(dayjs(state.read1Date))
        })

        return outOfOrder ? [ORDERING_MESSAGE] : []
      },
      shortGapWarnings(): string[] {
        const shortGap = this.registerStates.some((state) => {
          if (!usesTwoReads(this.mode)) return false
          if (!state.read1Date || !state.read2Date) return false

          return isShortGap(dayjs(state.read1Date), dayjs(state.read2Date))
        })

        return shortGap ? [SHORT_GAP_MESSAGE] : []
      },
      coarseUnitWarnings(): string[] {
        return this.registerStates.some((state) =>
          isCoarseVolumeUnit(state.unit),
        )
          ? [COARSE_UNIT_MESSAGE]
          : []
      },
      /**
       * SoNumberField emits 0 for an empty box, so a cleared read is
       * indistinguishable from a typed zero. Treat non-positive as "not a
       * read" - estimating from it would quietly produce a figure computed
       * against zero.
       */
      emptyReadErrors(): string[] {
        const hasZeroRead = this.registerStates.some((state) => {
          const reads = usesTwoReads(this.mode)
            ? [state.read1Value, state.read2Value]
            : [state.read2Value]

          return reads.some((value) => value !== null && value <= 0)
        })

        return hasZeroRead ? [EMPTY_READ_MESSAGE] : []
      },
      /** Backdating is allowed, but it should be said rather than implied. */
      backdatedWarnings(): string[] {
        if (!usesTargetDate(this.mode)) return []

        const backdated = this.registerStates.some(
          (state) =>
            state.read2Date &&
            dayjs(this.targetDate).isBefore(dayjs(state.read2Date)),
        )

        return backdated ? [BACKDATED_MESSAGE] : []
      },
      /**
       * Gas only, and blocking rather than defaulted: without the unit the
       * estimate is silently wrong instead of absent.
       */
      unitErrors(): string[] {
        if (this.meterpoint.isElectricity) return []

        return this.registerStates.some((state) => state.unit === null)
          ? [UNIT_REQUIRED_MESSAGE]
          : []
      },
      blockingErrors(): string[] {
        return [
          ...this.orderingErrors,
          ...this.emptyReadErrors,
          ...this.unitErrors,
        ]
      },
      warnings(): string[] {
        return [
          ...this.blockingErrors,
          ...this.shortGapWarnings,
          ...this.coarseUnitWarnings,
          ...this.backdatedWarnings,
        ]
      },
      request(): IEstimateRequest | null {
        if (this.blockingErrors.length) return null

        const registers = this.registerStates
          .map((state) => this.toRegisterInput(state))
          .filter((input): input is IEstimateRegisterInput => input !== null)

        if (
          !registers.length ||
          registers.length !== this.registerStates.length
        ) {
          return null
        }

        return {
          mode: toApiMode(this.mode),
          fuelType: toFuelType(this.meterpoint),
          seasonal: this.appliesSeasonality,
          // The endpoint requires a target date in every mode, and ignores it
          // when only the annual figure is wanted (SO-32399, issue 4).
          targetDate: formatDateDashed(dayjs(this.targetDate)),
          registers,
        }
      },
      resultRows() {
        return buildResultRows(
          this.estimate,
          this.lastRequest,
          this.registers,
          dayjs(this.targetDate),
        )
      },
      validationErrors(): string[] {
        return this.estimate?.validationErrors ?? []
      },
      calorificValueApplied(): number | null {
        return this.estimate?.calorificValueApplied ?? null
      },
      volumeUnitLabel(): string {
        if (this.meterpoint.isElectricity) return ""

        const unit = this.registerStates[0]?.unit
        return unit ? VOLUME_UNIT_LABELS[unit] : ""
      },
    },
    watch: {
      request: {
        handler(
          request: IEstimateRequest | null,
          previous: IEstimateRequest | null,
        ) {
          // A refetched meter point rebuilds the request without changing it,
          // and re-estimating would clear the submitted confirmation (SO-33700)
          if (isEqual(request, previous)) return

          if (!request) {
            this.estimate = null
            this.requestFailed = false
            return
          }

          this.debouncedFetch?.()
        },
      },
      /*
        Submitting a read refetches the account's meter points, which hands us
        a new object for the same meter point. Keep the agent's inputs through
        that - on a rebill they estimate the same register again and again - and
        only start over when they are looking at something else (SO-33700).
      */
      meterpoint(meterpoint: IMeterpoint, previous: IMeterpoint) {
        if (!isSameRegisterSet(meterpoint, previous)) this.initialiseState()
      },
    },
    created() {
      this.debouncedFetch = debounce(this.fetchEstimate, DEBOUNCE_MS)
      this.initialiseState()
    },
    beforeUnmount() {
      // Closing the modal or switching tab must not leave a POST queued.
      this.debouncedFetch?.cancel()
    },
    methods: {
      initialiseState() {
        this.debouncedFetch?.cancel()
        this.estimate = null
        this.lastRequest = null
        this.requestFailed = false
        this.registerStates = this.registers.map((register) => ({
          registerId: register.id,
          read1Id: null,
          read1Value: null,
          read1Date: null,
          read2Id: null,
          read2Value: null,
          read2Date: null,
          eacAq: register.usage,
          unit: volumeUnitFor(register),
        }))
      },
      patchRegister(index: number, patch: RegisterFormPatch) {
        const current = this.registerStates[index]
        if (!current) return

        this.registerStates.splice(index, 1, { ...current, ...patch })
      },
      toRegisterInput(
        state: IRegisterFormState,
      ): IEstimateRegisterInput | null {
        const register = this.registers.find(
          (candidate) => candidate.id === state.registerId,
        )
        if (!register || state.read2Value === null || !state.read2Date) {
          return null
        }

        const input: IEstimateRegisterInput = {
          // The value boxes render to DISPLAY_DECIMAL_PLACES, so send what the
          // agent can see. Sending 1234.5 while showing 1235 makes the estimate
          // unreproducible from the form.
          registerName: registerApiName(register),
          read2Value: toSentRead(state.read2Value),
          read2Date: formatDateDashed(dayjs(state.read2Date)),
          // No registerDigits: the gateway no longer carries the register's
          // real digit count (SO-23018), and guessing it from the latest read
          // is worse than the backend's own default - 00123 recorded as 123
          // would say the register rolls at 1000. SO-32539 restores it. Decimal
          // places is a display decision of ours, not a guess, so it stays.
          registerDecimalPlaces: DISPLAY_DECIMAL_PLACES,
        }

        if (!this.meterpoint.isElectricity) {
          if (state.unit === null) return null

          input.unit = state.unit
        }

        if (usesTwoReads(this.mode)) {
          if (state.read1Value === null || !state.read1Date) return null

          input.read1Value = toSentRead(state.read1Value)
          input.read1Date = formatDateDashed(dayjs(state.read1Date))

          return input
        }

        if (state.eacAq === null) return null
        input.eacAq = state.eacAq

        return input
      },
      async fetchEstimate() {
        const request = this.request
        if (!request) return

        // A response is stale once the inputs have moved on. That applies to a
        // rejection as much as a success: an older request failing must not
        // wipe out a newer estimate already on screen, and must not clear the
        // loading hint while the newer one is still in flight - either would
        // leave the panel showing a figure that does not match the inputs.
        const isStale = () => !isEqual(request, this.request)

        this.isLoading = true
        this.requestFailed = false

        try {
          const estimate = await MeterReadEstimatorService.estimate(request)
          if (isStale()) return

          this.estimate = estimate
          this.lastRequest = request
        } catch {
          if (isStale()) return

          this.estimate = null
          this.lastRequest = null
          this.requestFailed = true
        } finally {
          if (!isStale()) this.isLoading = false
        }
      },
    },
  })
</script>

<style scoped>
  /* Target date and the seasonality toggle share one line. */
  .estimator-controls {
    display: flex;
    align-items: flex-end;
    gap: 24px;
    flex-wrap: wrap;
  }

  .estimator-controls > * {
    flex: 0 1 auto;
    min-width: 0;
  }

  /*
    The date field carries a label above its box, the toggle does not, so
    aligning the two rows would sit the switch against the label. Giving the
    toggle a box the same height as the field and centring inside it lines the
    switch up with the input itself. 48px is the field height set in
    SoDatePicker.
  */
  .estimator-controls__toggle {
    display: flex;
    align-items: center;
    height: 48px;
  }
</style>
